import * as React from "react";
import { createPortal } from "react-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@hp/ui";
import type { ClaimCandidate, ClaimCandidateStatus } from "@hp/types";
import { updateClaimCandidateStatus } from "../data/index";
import {
  type ClaimActionPreset,
  type ClaimWorkflowAction,
  workflowActionsForPreset,
} from "../lib/claimWorkflowActions";
import { CLAIMS_CHANGED_MESSAGE } from "../lib/demoMode";
import { ReasonModal } from "./ReasonModal";

export interface ClaimActionButtonsProps {
  candidate: ClaimCandidate;
  preset: ClaimActionPreset;
  size?: "sm" | "md";
  /** Stop the click from bubbling to a parent row's onClick (used inside DataTable rows). */
  stopPropagation?: boolean;
  onChanged?: (updated: ClaimCandidate) => void;
}

/**
 * Status-aware workflow actions for one ClaimCandidate. Buttons are scoped to
 * the report view (review / pipeline / recovered) so each row shows only the
 * moves that make sense there — e.g. one-click "To Potential".
 */
export function ClaimActionButtons({
  candidate,
  preset,
  size = "sm",
  stopPropagation,
  onChanged,
}: ClaimActionButtonsProps) {
  const queryClient = useQueryClient();
  const [pendingReasonAction, setPendingReasonAction] = React.useState<ClaimWorkflowAction | null>(null);
  const actions = workflowActionsForPreset(candidate.status, preset);

  const mutation = useMutation({
    mutationFn: async (variables: {
      status: ClaimCandidateStatus;
      rejection_reason?: string | null;
      via?: ClaimCandidateStatus[];
    }) => {
      const { via = [], ...final } = variables;
      let reached: ClaimCandidateStatus = candidate.status;
      try {
        for (const step of via) {
          await updateClaimCandidateStatus(candidate.id, { status: step });
          reached = step;
        }
        return await updateClaimCandidateStatus(candidate.id, final);
      } catch (error) {
        // Partial chain: put the claim back where it started when the state machine allows, so it never
        // stays stranded in a status that isn't shown anywhere.
        if (reached !== candidate.status && candidate.status === "identified") {
          await updateClaimCandidateStatus(candidate.id, { status: "identified" }).catch(() => undefined);
        }
        throw error;
      }
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["claim-candidates"] });
      queryClient.invalidateQueries({ queryKey: ["claim-candidates", candidate.id] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["company-agreements"] });
      // Shown inside the Overview's claims modal (an iframe with its own query cache): tell the page behind it.
      if (window.parent !== window) window.parent.postMessage(CLAIMS_CHANGED_MESSAGE, window.location.origin);
      setPendingReasonAction(null);
      onChanged?.(updated);
    },
  });

  function stop(event: React.SyntheticEvent) {
    if (stopPropagation) event.stopPropagation();
  }

  function runAction(action: ClaimWorkflowAction, reason?: string) {
    const trimmedReason = reason?.trim();
    mutation.mutate({
      status: action.targetStatus,
      rejection_reason: action.requiresReason && trimmedReason ? trimmedReason : null,
      via: action.via,
    });
  }

  if (actions.length === 0) {
    return <span className="text-xs text-zinc-400">—</span>;
  }

  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      onClick={stop}
      onMouseDown={stop}
    >
      {actions.map((action) => (
        <Button
          key={`${action.label}-${action.targetStatus}`}
          type="button"
          size={size}
          variant={action.variant}
          disabled={mutation.isPending}
          onMouseDown={stop}
          onClick={(event) => {
            stop(event);
            if (action.requiresReason) {
              setPendingReasonAction(action);
              return;
            }
            runAction(action);
          }}
        >
          {action.label}
        </Button>
      ))}

      {pendingReasonAction
        ? createPortal(
            <ReasonModal
              open
              title={pendingReasonAction.reasonTitle ?? "Confirm action"}
              description={pendingReasonAction.reasonDescription ?? "A reason is required for this change."}
              confirmLabel={pendingReasonAction.confirmLabel ?? "Confirm"}
              confirmVariant={pendingReasonAction.confirmVariant ?? "primary"}
              optional={pendingReasonAction.reasonOptional}
              submitting={mutation.isPending}
              onCancel={() => setPendingReasonAction(null)}
              onConfirm={(reason) => runAction(pendingReasonAction, reason)}
            />,
            document.body,
          )
        : null}

      {mutation.isError ? (
        <span className="text-xs text-red-600">
          {mutation.error instanceof Error ? mutation.error.message : "Update failed"}
        </span>
      ) : null}
    </div>
  );
}
